/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { ShimmerText } from '../../compact-research-steps/ShimmerText';
import { EllipsesAnimation } from '../../primitives/ellipses-animation/EllipsesAnimation';
import { MorphingShapeLoader } from '../../primitives/morphing-shape-loader/MorphingShapeLoader';
import { LazyAnimatedRovoIcon } from '../../primitives/rovo-animated-icon/LazyAnimatedRovoIcon';

const styles = cssMap({
	root: {
		height: '24px',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'left',
		flexWrap: 'nowrap',
		alignItems: 'center',
	},
	// When bottomAdornment is present, the root height is auto and it grows to fill available space
	rootWithAdornment: {
		flex: '1',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'left',
		flexWrap: 'nowrap',
		alignItems: 'center',
		gap: token('space.100'),
	},
	iconContainer: {
		marginRight: token('space.025'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	// Column that holds the text row + bottomAdornment, growing to fill available space
	textColumn: {
		flex: '1',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	// Row within textColumn that holds the text + ellipses
	textRow: {
		height: '24px',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
	},
	text: {
		color: token('color.text'),
		font: token('font.body'),
		wordBreak: 'break-word',
	},
	textSmall: {
		color: token('color.text'),
		font: token('font.body.small'),
		wordBreak: 'break-word',
	},
	textNoWrap: {
		color: token('color.text'),
		font: token('font.body'),
		whiteSpace: 'nowrap',
		overflow: 'hidden',
	},
	textNoWrapSmall: {
		color: token('color.text'),
		font: token('font.body.small'),
		whiteSpace: 'nowrap',
		overflow: 'hidden',
	},
	ellipsesContainer: {
		flex: 1,
	},
	iconContainerShrinkable: {
		marginRight: token('space.025'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: 0,
	},
	textColumnShrinkable: {
		flex: '1',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		minWidth: 0,
	},
	iconSlot: {
		// `none` (0 0 auto) rather than `flexShrink: 0`, as this map uses the `flex` shorthand.
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
	},
	textTruncateEllipsis: {
		color: token('color.text'),
		font: token('font.body'),
		minWidth: 0,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	textTruncateEllipsisSmall: {
		color: token('color.text'),
		font: token('font.body.small'),
		minWidth: 0,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	// Wraps at any letter and shows only the first line, so overflow clips at a whole letter rather than mid-glyph.
	// Justify stretches that clipped line to the box edge so the dots sit right after the last letter.
	textTruncateClip: {
		color: token('color.text'),
		font: token('font.body'),
		minWidth: 0,
		// Matches the font.body line height.
		maxBlockSize: '20px',
		overflow: 'hidden',
		whiteSpace: 'normal',
		wordBreak: 'break-all',
		textAlign: 'justify',
	},
	textTruncateClipSmall: {
		color: token('color.text'),
		font: token('font.body.small'),
		minWidth: 0,
		// Matches the font.body.small line height.
		maxBlockSize: '16px',
		overflow: 'hidden',
		whiteSpace: 'normal',
		wordBreak: 'break-all',
		textAlign: 'justify',
	},
	ellipsesContainerNoShrink: {
		flex: '1 0 auto',
	},
});

const getTextStyleKey = (
	textSize: 'default' | 'small',
	showEllipsis: boolean,
): keyof typeof styles => {
	if (textSize === 'small') {
		if (showEllipsis) {
			return 'textNoWrapSmall';
		}

		return 'textSmall';
	}

	if (showEllipsis) {
		return 'textNoWrap';
	}

	return 'text';
};

const getTruncatedTextStyleKey = (
	textSize: 'default' | 'small',
	isShowingAnimatedDots: boolean,
): keyof typeof styles => {
	if (isShowingAnimatedDots) {
		return textSize === 'small' ? 'textTruncateClipSmall' : 'textTruncateClip';
	}

	return textSize === 'small' ? 'textTruncateEllipsisSmall' : 'textTruncateEllipsis';
};

type RovoThinkingIndicatorProps = {
	// text shown between the Rovo icon and ellipses
	title: string;
	isAnimated?: boolean;
	// true = Rovo icon, ReactNode = custom icon (e.g. Agent avatar), false = no icon.
	// Has no effect on the morphing icon when animation is enabled — use disableMorphingAnimation for that.
	icon?: boolean | ReactNode;
	// whether to apply shimmer and loading icon animation effect to the title text
	animation?: boolean;
	// when true, suppresses the morphing animation icon and falls back to the icon prop
	disableMorphingAnimation?: boolean;
	// whether to show the ellipsis dots (default: true)
	showEllipsis?: boolean;
	// text size variant for compact surfaces
	textSize?: 'default' | 'small';
	testId?: string;
	// optional content rendered below the text (but not the icon), e.g. a progress bar
	bottomAdornment?: ReactNode;
};

const getLoadingMorphingIcon = () => {
	return <MorphingShapeLoader testId="morphing-loader" />;
};

const getIcon = (icon: ReactNode | boolean): ReactNode => {
	if (React.isValidElement(icon)) {
		return icon;
	} else if (icon) {
		return (
			<LazyAnimatedRovoIcon alt="Rovo" testId="rovo-loading-animation" width="24px" height="24px" />
		);
	}

	return null;
};

export const RovoThinkingIndicator = ({
	title,
	isAnimated = true,
	icon = true,
	animation = false,
	disableMorphingAnimation = false,
	showEllipsis = true,
	textSize = 'default',
	testId,
	bottomAdornment,
}: RovoThinkingIndicatorProps): JSX.Element => {
	const enableAnimation = animation;

	const isTruncationEnabled = showEllipsis && fg('rovo_thinking_indicator_text_truncation');
	const isShowingAnimatedDots = showEllipsis && isAnimated;

	const iconNode =
		enableAnimation && !disableMorphingAnimation ? getLoadingMorphingIcon() : getIcon(icon);
	const textStyleKey = isTruncationEnabled
		? getTruncatedTextStyleKey(textSize, isShowingAnimatedDots)
		: getTextStyleKey(textSize, showEllipsis);
	const textNode = (
		<Box xcss={styles[textStyleKey]}>
			{enableAnimation ? (
				<ShimmerText
					size={textSize === 'small' ? 'small' : 'medium'}
					truncate={isTruncationEnabled && !isShowingAnimatedDots}
				>
					{title}
				</ShimmerText>
			) : (
				title
			)}
		</Box>
	);
	const ellipsesNode = showEllipsis ? (
		<Box xcss={isTruncationEnabled ? styles.ellipsesContainerNoShrink : styles.ellipsesContainer}>
			<EllipsesAnimation isAnimated={isAnimated} />
		</Box>
	) : null;

	if (bottomAdornment) {
		return (
			<Box xcss={styles.rootWithAdornment} testId={testId}>
				<Box xcss={styles.iconContainer}>{iconNode}</Box>
				<Box xcss={isTruncationEnabled ? styles.textColumnShrinkable : styles.textColumn}>
					<Box xcss={styles.textRow}>
						{textNode}
						{ellipsesNode}
					</Box>
					{bottomAdornment}
				</Box>
			</Box>
		);
	}

	if (isTruncationEnabled) {
		return (
			<Box xcss={styles.root} testId={testId}>
				<Box xcss={styles.iconContainerShrinkable}>
					{iconNode ? <Box xcss={styles.iconSlot}>{iconNode}</Box> : null}
					{textNode}
				</Box>
				{ellipsesNode}
			</Box>
		);
	}

	return (
		<Box xcss={styles.root} testId={testId}>
			<Box xcss={styles.iconContainer}>
				{iconNode}
				{textNode}
			</Box>
			{ellipsesNode}
		</Box>
	);
};
